import React, { useEffect, useRef, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import {
  Button,
  Col,
  Container,
  Form,
  Image,
  OverlayTrigger,
  Pagination,
  Popover,
  Row,
  Table,
} from "react-bootstrap";
import { FaDownload, FaFilter } from "react-icons/fa";
import S from "../lib/Security";
import N from "../lib/Network";
import { Utils, createDateFormat, deepEqual, parseObject } from "../lib/Utils";
import * as XLSX from "xlsx";
import OcrFilterPopover from "./OcrFilterPopover";
import Loader from "./Loader";

const BACKEND_DATA_FETCH_QUANTITY = 1000;

const metadataSchema = {
  totalPages: 0,
  currentPage: 0,
  hasContent: false,
  isFirst: true,
  isLast: false,
  totalElements: 0,
  numberOfElements: 1000,
};

const filterParams = {
  tenantName: "",
  startDate: "",
  endDate: "",
  order: "descending"
};

const baseActiveData = {
  displayItems: [],
  type: "base",
  filterParams: filterParams,
  itemsPerPage: 50,
  metadata: metadataSchema,
  paginationPage: 1,
};

const OcrReportTable = () => {
  const [ocrData, setOcrData] = useState([]);
  const [hasRerendered, setRerendered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const buttonRef = useRef(null);
  const [selectedMethod, setSelectedMethod] = useState(() => () => {});
  const [filteredData, setFilteredData] = useState([]);

  const [startPage, setStartPage] = useState(1);
  const [activeData, setActiveData] = useState(baseActiveData);
  const [preFilterMetadata, setPreFilterMetadata] = useState(metadataSchema);

  const headerMapping = {
    invNumber: "Invoice Number",
    ocrRefId: "OCR Reference Id",
    tenantName: "Tenant Name",
    creationDate: "OCR Retrieval Date",
    sftpPath: "Document Location",
    pages: "Number of Pages",
  };

  const keysToExclude = [];
  const exportToExcel = (data) => {
    const date = createDateFormat.createCurrentDate();

    const transformedData = data.map((item) => {
      const newItem = {};
      Object.keys(headerMapping).forEach((key) => {
        if (!keysToExclude.includes(key)) {
          newItem[headerMapping[key]] = item[key];
        }
      });
      return newItem;
    });

    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Report-${date}`);

    XLSX.writeFile(workbook, "OCR_Invoice_report.xlsx");
  };

  const fetchData = (pageNo) => {
    setLoading(true);
    const url = `getOcrReport?page=${pageNo}&size=${BACKEND_DATA_FETCH_QUANTITY}`;
    N.getApi(url)
      .then((res) => {
        var response = S.getDecryptedJSON(res);
        const parsedData = JSON.parse(response.responseJson);
        const metadata = parsedData.Metadata;
        delete parsedData.Metadata;
        let frontendMetadata = parseObject(metadata, metadataSchema);
        frontendMetadata.currentPage++;
        if (frontendMetadata.currentPage === metadata.totalPages)
          frontendMetadata.isLast = true;
        setOcrData((prevData) => [
          ...prevData,
          ...parsedData.Payload.ocrReportList,
        ]);
        setLoading(false);
        setPreFilterMetadata(frontendMetadata)
        setActiveData((prevData) => ({
          ...prevData,
          type: "base",
          displayItems: parsedData.Payload.ocrReportList || [],
          filterParams: filterParams,
          metadata: frontendMetadata,
          paginationPage: 1,
        }));
        baseSetLoadingMethod(fetchData, frontendMetadata.currentPage)
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!hasRerendered) {
      Utils.getKeys();
      fetchData(activeData.metadata.currentPage);
    }
    setRerendered(true);
  }, [hasRerendered]);

  const baseSetLoadingMethod = (method, param) => {
    if (method) {
      setSelectedMethod(() => () => method(param));
    } else {
      setSelectedMethod(() => () => {});
    }
  };

  const dataSet = activeData.type === "base" ? ocrData : filteredData;

  const dataToDisplay = Array.isArray(dataSet) ? dataSet : [];
  const indexOfLastItem = activeData?.paginationPage * activeData?.itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - activeData?.itemsPerPage;
  const currentItems = dataToDisplay?.slice(indexOfFirstItem, indexOfLastItem);

  const totalItems = dataSet?.length || 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / activeData?.itemsPerPage)
  );

  const handlePageChange = (pageNumber) => {
    const page = parseInt(pageNumber, 10);
    if (isNaN(page) || page < 1 || page > totalPages) return;
    setActiveData((prev) => ({ ...prev, paginationPage: page }));

    const newStartPage =
      Math.floor((page - 1) / activeData?.itemsPerPage) *
        activeData?.itemsPerPage +
      1;
    setStartPage(newStartPage);
  };

  const getPageRange = () => {
    const maxVisiblePages = 5;
    const startPage = Math.max(1, activeData.paginationPage - maxVisiblePages);
    const endPage = Math.min(
      totalPages,
      activeData.paginationPage + maxVisiblePages
    );

    const pages = [];
    for (let page = startPage; page <= endPage; page++) {
      pages.push(page);
    }
    return pages;
  };

  return (
    <div id="content-wrapper" className="d-flex flex-column">
      {<Sidebar />}
      {<Topbar />}
      <div id="wrapper">
        <div id="content">
          {/* <!-- Begin Page Content --> */}
          <Container fluid>
          {loading && <Loader showLoader={loading} />}
            {error && <div>Error occurred fetching data</div>}
            {!(loading || error) && (
              <>
            <Row>
              <Col xs={12} md={12} lg={12}>
                <div className="card dash-data-card">
                  <div className="card-header pl-4 pr-4 d-flex justify-content-between">
                    <h5 className="headerTitle  mt-1">OCR Report</h5>
                    <div className="d-flex h-100">
                      <Button
                        className="btn btn-sm btn-primary shadow-sm px-4"
                        onClick={() => exportToExcel(dataToDisplay)}
                      >
                        <FaDownload className="fas" />
                      </Button>
                      <OcrFilterPopover 
                        buttonRef={buttonRef}
                        activeData={activeData}
                        setActiveData={setActiveData}
                        setLoading={setLoading}
                        setError={setError}
                        setFilteredData={setFilteredData}
                        size={BACKEND_DATA_FETCH_QUANTITY}
                        baseActiveData={baseActiveData}
                        preFilterMetadata={preFilterMetadata}
                        setPreFilterMetadata={setPreFilterMetadata}
                        baseSetLoadingMethod={baseSetLoadingMethod}
                        ocrData={ocrData}
                        dataToDisplay={dataToDisplay}
                        metadataSchema={metadataSchema}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mt-3 pl-2 pr-2">
              <div className="border rounded pt-2">
                {dataToDisplay.slice(indexOfFirstItem, indexOfLastItem) ? (
                  <Table
                    bordered
                    style={{ borderRadius: "5px", overflow: "hidden" }}
                  >
                    <thead>
                      <tr>
                        {Object.keys(headerMapping).map((item, i) => {
                          if (!keysToExclude.includes(item))
                            return <th key={i}>{headerMapping[item]}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems?.map((pageItem, i) => {
                        return (
                          <tr key={i}>
                            <td>{pageItem.invNumber}</td>
                            <td>{pageItem.ocrRefId}</td>
                            <td>{pageItem.tenantName}</td>
                            <td>{pageItem.creationDate}</td>
                            <td>{pageItem.sftpPath}</td>
                            <td>{pageItem.pages}</td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td style={{ border: "none" }}></td>
                        <td style={{ border: "none" }}></td>
                        <td style={{ border: "none" }}></td>
                        <td style={{ border: "none" }}></td>
                        <td
                          style={{
                            border: "none",
                            fontWeight: 600,
                            textAlign: "end",
                          }}
                        >
                          Total:
                        </td>
                        <td>
                          {dataToDisplay?.reduce(
                            (acc, item) => acc + Number(item.pages),
                            0
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <div className="d-flex justify-content-center pb-2">
                    <span className="fs-6 fw-light">Fetching data</span>
                  </div>
                )}
              </div>
            </Row>

            {/* Pagination Controls */}
            <Row className="my-3 mx-auto h-100 d-flex justify-content-between flex-grow-1">
              <Pagination style={{ width: "min-content" }}>
                {/* First Page Icon */}
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={activeData.paginationPage === 1}
                />

                {/* Previous Page Icon */}
                <Pagination.Prev
                  onClick={() =>
                    handlePageChange(activeData.paginationPage - 1)
                  }
                  disabled={activeData.paginationPage === 1}
                />

                {/* Page Numbers */}
                {getPageRange().map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === activeData.paginationPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}

                {/* Next Page Icon */}
                <Pagination.Next
                  onClick={() =>
                    handlePageChange(activeData.paginationPage + 1)
                  }
                  disabled={activeData.paginationPage === totalPages}
                />

                {/* Last Page Icon */}
                <Pagination.Last
                  onClick={() => handlePageChange(totalPages)}
                  disabled={activeData.paginationPage === totalPages}
                />
              </Pagination>

              {totalItems > activeData.itemsPerPage && (
                <div
                  style={{ width: "min-content" }}
                  className="d-flex h-100 justify-content-between"
                >
                  <Button
                    onClick={() => selectedMethod()}
                    className={`text-nowrap h-100 fw-bold px-4 ${
                      activeData.metadata.isLast &&
                      "pe-none bg-secondary border border-secondary"
                    }`}
                    style={{
                      minWidth: "fit-content",
                      maxWidth: "fit-content",
                    }}
                  >
                    Load more
                  </Button>
                  <div className="d-flex h-100 align-items-center pt-2 ">
                    <span className="text-nowrap ml-4">Go to page </span>
                    <input
                      min="1"
                      max={totalPages}
                      type="number"
                      style={{
                        width: "2rem",
                        height: "100%",
                        margin: "0 0.6rem",
                      }}
                      onBlur={(e) => handlePageChange(e.target.value)}
                    />
                    <span className="text-nowrap">
                      {" "}
                      out of pages <span className="fw-bold">{totalPages}</span>
                    </span>
                  </div>
                </div>
              )}
            </Row>
            </>
          )}
          </Container>
          {/* <!-- /.container-fluid --> */}
        </div>
      </div>
    </div>
  );
};

export default OcrReportTable;
